<script>
import { mapGetters } from 'vuex'

/**
 * Shown to an admin approving a class whose students include addresses Google
 * could not validate, or has a correction for. Informational: the admin can
 * approve anyway, and nothing else is held back.
 */
export default {
  name: 'AddressApprovalWarning',
  props: {
    result: { type: Object, required: true },
  },
  computed: {
    ...mapGetters(['stateOptions']),
    flagged () {
      return this.result.students.filter(s => s.status === 'fix' || s.status === 'unverified')
    },
  },
  methods: {
    formatAddress ({ address1, address2, city, stateId, zip } = {}) {
      if (!address1 && !city) return 'No address on file'
      const state = this._.find(this.stateOptions, { value: stateId })
      const stateZip = [state ? state.label : '', zip].filter(Boolean).join(' ')
      return [[address1, address2].filter(Boolean).join(' '), [city, stateZip].filter(Boolean).join(', ')]
        .filter(Boolean)
        .join(', ')
    },
  },
}
</script>

<template>
  <q-modal class="sl-modal" id="addressApprovalWarning" :value="true" no-backdrop-dismiss @hide="$emit('cancel')">
    <q-modal-layout>
      <q-toolbar slot="header" color="warning">
        <q-toolbar-title>
          <q-icon name="warning" />
          Student Address Warning
        </q-toolbar-title>
        <q-icon @click.native="$emit('cancel')" name="clear" />
      </q-toolbar>
      <div class="address-approval-warning">
        <p>
          Of the <b>{{ result.total }}</b> students in this class,
          <template v-if="result.unverified">
            <b>{{ result.unverified }}</b> {{ result.unverified === 1 ? 'has an address' : 'have addresses' }} Google could not validate
          </template>
          <template v-if="result.unverified && result.fix"> and </template>
          <template v-if="result.fix">
            <b>{{ result.fix }}</b> {{ result.fix === 1 ? 'has an address' : 'have addresses' }} Google has a correction for
          </template>.
          You can still approve the class.
        </p>
        <p v-if="result.unchecked" class="text-faded">
          {{ result.unchecked }} {{ result.unchecked === 1 ? 'address' : 'addresses' }} could not be checked right now.
        </p>
        <table>
          <thead>
            <tr><th>Student</th><th>Address</th><th>Status</th></tr>
          </thead>
          <tbody>
            <tr v-for="student in flagged" :key="student.studentId">
              <td>{{ student.name }}</td>
              <td>
                {{ formatAddress(student.address) }}
                <div v-if="student.suggestion" class="suggestion">
                  Google suggests: {{ formatAddress(student.suggestion) }}
                </div>
              </td>
              <td>
                <span v-if="student.status === 'fix'" class="text-warning">Fix Available</span>
                <span v-else class="text-negative">Not Validated</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <q-toolbar slot="footer">
        <q-toolbar-title></q-toolbar-title>
        <q-btn color="positive" icon="done" label="Approve Anyway" @click.native="$emit('approve')" />&nbsp;
        <q-btn outline color="faded" label="Cancel" @click.native="$emit('cancel')" />
      </q-toolbar>
    </q-modal-layout>
  </q-modal>
</template>

<style lang="stylus">
.address-approval-warning
  padding 20px
  max-width 760px

  p
    margin 0 0 12px

  table
    width 100%
    border-collapse collapse
    font-size 13px

    th, td
      text-align left
      padding 6px 8px
      border-bottom 1px solid #eee
      vertical-align top

    th
      font-weight 600
      color #757575

  .suggestion
    margin-top 2px
    color #2e7d32
</style>
